/**
 * Снимок для сравнения: код редактора + текстовый ответ (поле в условии).
 */
export function buildAttemptSnapshot(codeSnapshot, studentTextAnswer) {
  return `${String(codeSnapshot ?? '').trim()}\n---\n${String(studentTextAnswer ?? '').trim()}`;
}

/**
 * Загрузить последнюю попытку по задаче и пункту списка.
 */
export async function fetchLatestAttempt(api, taskSlug, listItemId) {
  const params = { task: taskSlug };
  if (listItemId) params.task_list_item = listItemId;
  const res = await api.get('/tasks/attempts/', { params });
  const arr = Array.isArray(res.data) ? res.data : res.data?.results ?? [];
  return arr[0] ?? null;
}

/**
 * POST новая попытка или PATCH последней, если снимок совпадает.
 * testsResult — объект для merge в tests_result (runner + answer_*).
 */
export async function recordTaskAttempt(api, payload) {
  const {
    taskSlug,
    taskId,
    listItemId,
    lessonSessionId,
    codeSnapshot,
    studentTextAnswer,
    selfReportStatus,
    selfComment,
    testsResult,
  } = payload;

  const latest = await fetchLatestAttempt(api, taskSlug, listItemId);
  const newSnap = buildAttemptSnapshot(codeSnapshot, studentTextAnswer);
  const oldSnap = latest
    ? buildAttemptSnapshot(latest.code_snapshot, latest.tests_result?.student_text_answer)
    : null;

  const mergedForPatch =
    latest && typeof latest.tests_result === 'object' && latest.tests_result !== null
      ? { ...latest.tests_result, ...(testsResult || {}) }
      : { ...(testsResult || {}) };
  if (studentTextAnswer !== undefined && studentTextAnswer !== null) {
    mergedForPatch.student_text_answer = String(studentTextAnswer);
  }

  if (latest && newSnap === oldSnap) {
    await api.patch(`/tasks/attempts/${latest.id}/`, {
      self_report_status: selfReportStatus ?? latest.self_report_status,
      self_comment: selfComment !== undefined ? selfComment : latest.self_comment,
      code_snapshot: String(codeSnapshot ?? ''),
      tests_result: mergedForPatch,
    });
    return { mode: 'patch', id: latest.id };
  }

  const mergedForPost = { ...(testsResult || {}) };
  if (studentTextAnswer !== undefined && studentTextAnswer !== null) {
    mergedForPost.student_text_answer = String(studentTextAnswer);
  }

  await api.post('/tasks/attempts/', {
    task: taskId,
    lesson_session: lessonSessionId ? Number(lessonSessionId) : null,
    task_list_item: listItemId,
    code_snapshot: String(codeSnapshot ?? ''),
    tests_result: mergedForPost,
    self_report_status: selfReportStatus ?? 'NOT_TRIED',
    self_comment: selfComment || '',
  });
  return { mode: 'post' };
}

/** MVP: нормализация для сравнения с expected_answer */
export function normalizeAnswerText(s) {
  return String(s ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

export function isChoiceAnswerType(answerType) {
  return answerType === 'single_choice' || answerType === 'multiple_choice';
}

/** Id вариантов из строки expected_answer / ответа ученика: "a,c" → ["a","c"] */
export function parseChoiceIds(s) {
  return String(s ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Стабильная строка id для multiple_choice (сортировка, без дублей). */
export function formatChoiceIds(ids, { sort = true } = {}) {
  const cleaned = [...new Set((ids || []).map((id) => String(id).trim()).filter(Boolean))];
  if (sort) cleaned.sort((a, b) => a.localeCompare(b, 'en'));
  return cleaned.join(',');
}

export function getChoiceOptions(answerSpec) {
  const opts = answerSpec && typeof answerSpec === 'object' ? answerSpec.options : null;
  if (!Array.isArray(opts)) return [];
  return opts
    .filter((o) => o && o.id != null && String(o.id).trim() !== '')
    .map((o) => ({
      id: String(o.id).trim(),
      text: o.text == null ? '' : String(o.text),
    }));
}

/** Буквы a, b, … по позиции на экране (после перемешивания). */
export function withChoiceDisplayLabels(options) {
  const used = [];
  return (options || []).map((opt) => {
    const label = nextChoiceOptionId(used);
    used.push(label);
    return { ...opt, label };
  });
}

export function shuffleArray(items, rng = Math.random) {
  const arr = [...(items || [])];
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const tmp = arr[i];
    arr[i] = arr[j];
    arr[j] = tmp;
  }
  return arr;
}

/**
 * Перемешать варианты и повесить отображаемые буквы.
 * Порядок кэшируется в sessionStorage по seed (список + задача),
 * чтобы при возврате к вопросу и после обновления страницы он не прыгал.
 * Проверка ответа по-прежнему идёт по исходному id.
 */
export function resolveShuffledChoiceOptions(options, { seed = '', shuffle = true } = {}) {
  const raw = options || [];
  if (!shuffle || raw.length <= 1) {
    return withChoiceDisplayLabels(raw);
  }
  const key = seed ? `choiceShuffle:${seed}` : '';
  let savedIds = null;
  if (key && typeof sessionStorage !== 'undefined') {
    try {
      const saved = sessionStorage.getItem(key);
      if (saved) savedIds = JSON.parse(saved);
    } catch {
      savedIds = null;
    }
  }
  const byId = new Map(raw.map((o) => [o.id, o]));
  let ordered = Array.isArray(savedIds)
    ? savedIds.map((id) => byId.get(String(id))).filter(Boolean)
    : [];
  if (ordered.length !== raw.length) {
    ordered = shuffleArray(raw);
    if (key && typeof sessionStorage !== 'undefined') {
      try {
        sessionStorage.setItem(key, JSON.stringify(ordered.map((o) => o.id)));
      } catch {
        /* quota / private mode */
      }
    }
  }
  return withChoiceDisplayLabels(ordered);
}

export function formatChoiceRevealText(expectedAnswer, options) {
  const ids = parseChoiceIds(expectedAnswer);
  if (!ids.length) return '';
  const byId = new Map(
    (options || []).map((o) => [normalizeAnswerText(o.id), o]),
  );
  return ids
    .map((id) => {
      const opt = byId.get(normalizeAnswerText(id));
      if (!opt) return id;
      const letter = opt.label || opt.id;
      const text = String(opt.text ?? '').trim();
      return text ? `${letter}. ${text}` : String(letter);
    })
    .join('\n');
}

function checkChoiceAnswer(studentAnswer, expectedAnswer, answerType) {
  if (expectedAnswer == null || String(expectedAnswer).trim() === '') return null;
  if (answerType === 'multiple_choice') {
    const a = new Set(parseChoiceIds(studentAnswer).map(normalizeAnswerText));
    const b = new Set(parseChoiceIds(expectedAnswer).map(normalizeAnswerText));
    if (b.size === 0) return null;
    if (a.size !== b.size) return false;
    for (const id of a) {
      if (!b.has(id)) return false;
    }
    return true;
  }
  return normalizeAnswerText(studentAnswer) === normalizeAnswerText(expectedAnswer);
}

/**
 * Проверка ответа. Для choice — сравнение id (у multiple — как множества).
 * @returns {boolean|null} null если проверять нечего
 */
export function checkAnswerAgainstExpected(studentAnswer, expectedAnswer, answerType) {
  if (isChoiceAnswerType(answerType)) {
    return checkChoiceAnswer(studentAnswer, expectedAnswer, answerType);
  }
  if (expectedAnswer == null || String(expectedAnswer).trim() === '') return null;
  return normalizeAnswerText(studentAnswer) === normalizeAnswerText(expectedAnswer);
}

/** Следующий id варианта: a, b, … z, затем aa, ab, … */
export function nextChoiceOptionId(existingIds) {
  const used = new Set((existingIds || []).map((id) => normalizeAnswerText(id)));
  const alphabet = 'abcdefghijklmnopqrstuvwxyz';
  for (const ch of alphabet) {
    if (!used.has(ch)) return ch;
  }
  for (let i = 0; i < alphabet.length; i += 1) {
    for (let j = 0; j < alphabet.length; j += 1) {
      const id = alphabet[i] + alphabet[j];
      if (!used.has(id)) return id;
    }
  }
  let n = 1;
  while (used.has(String(n))) n += 1;
  return String(n);
}
